import React, { useMemo, useState } from "react";
import * as Mui from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Pivot } from "../../dtos/pivot";

interface PivotEquipmentTabsProps {
  pivot: Pivot;
}

interface PumpOption {
  id: number;
  name: string;
  serialNumber: number;
}

interface ValveOption {
  id: number;
  label: string;
  serialNumber: number;
}

type TabKey = "valves" | "pumps" | "sensors";

interface EquipmentRow {
  name: string;
  status: string;
  detail: string;
  liveValue: string;
}

const StyledCardContent = Mui.withStyles((theme: any) => ({
  root: {
    backgroundColor: theme.cardHeaderColor
  }
}))(Mui.CardContent);

const tabItems: Array<{ key: TabKey; label: string }> = [
  { key: "valves", label: "Valves" },
  { key: "pumps", label: "Pumps" },
  { key: "sensors", label: "Sensors" }
];

const SAMPLE_PUMPS: PumpOption[] = [
  { id: 1, name: "Pump 1", serialNumber: 20001 },
  { id: 2, name: "Pump 2", serialNumber: 20002 },
  { id: 3, name: "Pump 3", serialNumber: 20003 },
  { id: 4, name: "Pump 4", serialNumber: 20004 },
  { id: 5, name: "Pump 5", serialNumber: 20005 }
];

const SAMPLE_VALVES: ValveOption[] = [
  { id: 1, label: "Valve 1", serialNumber: 10001 },
  { id: 2, label: "Valve 2", serialNumber: 10002 },
  { id: 3, label: "Valve 3", serialNumber: 10003 },
  { id: 4, label: "Valve 4", serialNumber: 10004 },
  { id: 5, label: "Valve 5", serialNumber: 10005 }
];

const getTabRows = (pivot: Pivot): Record<TabKey, EquipmentRow[]> => ({
  valves: SAMPLE_VALVES
    .filter(valve => (pivot.valve_ids ?? []).includes(valve.id))
    .map(valve => ({
      name: valve.label,
      status: pivot.online ? "Open" : "Closed",
      detail: `Serial ${valve.serialNumber}`,
      liveValue: ""
    })),
  pumps: SAMPLE_PUMPS
    .filter(pump => (pivot.pump_ids ?? []).includes(pump.id))
    .map(pump => ({
      name: pump.name,
      status: pivot.online ? pivot.status : "Offline",
      detail: `Serial ${pump.serialNumber}`,
      liveValue: `PSI ${pivot.pressure ?? 0} psi | Flow ${pivot.flow ?? 0} gpm | Current ${pivot.current ?? 0} A`
    })),
  sensors: [
    {
      name: "Moisture Sensor",
      status: "",
      detail: "",
      liveValue: ""
    }
  ]
});

export const PivotEquipmentTabs = ({ pivot }: PivotEquipmentTabsProps) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<TabKey>("valves");
  const rowsByTab = useMemo(() => getTabRows(pivot), [pivot]);
  const activeRows = rowsByTab[activeTab];
  const activeLabel = tabItems.find(item => item.key === activeTab)?.label ?? "Items";

  return (
    <Mui.Card className={classes.card} variant="outlined">
      <StyledCardContent>
        <div className={classes.cardHeader}>
          <Mui.Grid container justify="space-between" alignItems="center">
            <Mui.Grid item>
              <div className={classes.cardHeaderTitle}>Connected Equipment</div>
            </Mui.Grid>
            <Mui.Grid item>
              <div className={classes.headerActions}>
                <Mui.Button size="small" color="primary">
                  Add {activeLabel.slice(0, -1)}
                </Mui.Button>
                <Mui.Button size="small" color="primary">
                  Edit {activeLabel}
                </Mui.Button>
              </div>
            </Mui.Grid>
          </Mui.Grid>
        </div>
      </StyledCardContent>
      <Mui.CardContent>
        <Mui.Tabs
          value={activeTab}
          onChange={(_, value: TabKey) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}
        >
          {tabItems.map(item => (
            <Mui.Tab key={item.key} value={item.key} label={item.label} />
          ))}
        </Mui.Tabs>

        {activeRows.length === 0 ? (
          <Mui.Typography variant="body2" color="textSecondary" className={classes.emptyState}>
            No {activeTab} data available for this pivot yet.
          </Mui.Typography>
        ) : (
          <Mui.Grid container spacing={2}>
            {activeRows.map((row, index) => (
              <Mui.Grid item xs={12} key={`${activeTab}-${index}`}>
                <div className={classes.row}>
                  <Mui.Grid container spacing={2} alignItems="center">
                    <Mui.Grid item xs={12} md={3}>
                      <b>{row.name}</b>
                    </Mui.Grid>
                    <Mui.Grid item xs={12} md={2} className={row.status ? undefined : classes.hiddenCell}>
                      {row.status}
                    </Mui.Grid>
                    <Mui.Grid item xs={12} md={row.detail ? 3 : false} className={row.detail ? undefined : classes.hiddenCell}>
                      {row.detail}
                    </Mui.Grid>
                    <Mui.Grid item xs={12} md={row.liveValue ? (row.detail ? 4 : 7) : false} className={row.liveValue ? classes.liveValue : classes.hiddenCell}>
                      <b>{row.liveValue}</b>
                    </Mui.Grid>
                  </Mui.Grid>
                </div>
              </Mui.Grid>
            ))}
          </Mui.Grid>
        )}
      </Mui.CardContent>
    </Mui.Card>
  );
};

const useStyles = makeStyles((theme: any) => ({
  card: {
    width: "100%",
    marginBottom: theme.spacing(2)
  },
  cardHeader: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center"
  },
  cardHeaderTitle: {
    fontWeight: "bold"
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    "& button": {
      textTransform: "none",
      marginLeft: theme.spacing(1)
    }
  },
  tabs: {
    marginBottom: theme.spacing(2)
  },
  row: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  liveValue: {
    display: "flex",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start"
    }
  },
  hiddenCell: {
    display: "none"
  },
  emptyState: {
    paddingTop: theme.spacing(1)
  }
}));
